/**
 * Admin "Customer 360" — mirrors the dealer-app Contact360 page so an
 * admin auditing a dealership can see the same identity, activity, and
 * orbiting entities the dealer sees. Read-only: the existing
 * DealershipContactsPage edit dialog is the one place an admin mutates
 * core contact fields; tabs here only render data the dealer authored.
 */
import { useMemo, type ReactNode } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BellRing,
  CalendarCheck,
  Car,
  Heart,
  MessageSquare,
  Pencil,
  Plug,
  Settings,
  ShieldCheck,
  StickyNote,
  UserCheck,
  type LucideIcon,
} from "lucide-react";
import {
  Badge,
  Button,
  EmptyState,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from "@lumika/ui";
import { userDisplayLabel } from "@lumika/shared";
import type {
  Activity,
  Appointment,
  ConsentEvent,
  Contact,
  Lead,
  ContactTag,
  ContactVehicle,
  FollowUp,
  TradeIn,
  VehicleInterest,
} from "@lumika/shared";
import { adminApi } from "../../api/admin";
import { ContactAvatar } from "../../components/ContactAvatar";
import {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  humanize,
  summarizeChangeBody,
} from "../../lib/format";

// Per-contact tab lists: initial page of 20, "Show more" pulls the next
// page. The admin SDK list methods return `{items,total,page}` envelopes;
// this offset-pages them the same way the dealer-app Customer-360 does.
const TAB_PAGE_SIZE = 20;
// The merged timeline fans several of these lists in at once; cap each
// source so a contact with hundreds of rows doesn't pull them all.
const TIMELINE_PAGE_SIZE = 50;

function usePagedTabList<T extends { id: string }>(
  queryKey: readonly unknown[],
  fetchPage: (page: number) => Promise<{ items: T[]; total: number }>,
) {
  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });
}

/** "Show more" footer for the per-contact tab lists. */
function ShowMore({
  hasNextPage,
  isFetchingNextPage,
  onClick,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onClick: () => void;
}) {
  if (!hasNextPage) return null;
  return (
    <li>
      <Button
        variant="ghost"
        size="sm"
        disabled={isFetchingNextPage}
        onClick={onClick}
      >
        {isFetchingNextPage ? "Loading…" : "Show more"}
      </Button>
    </li>
  );
}

export function DealershipContactDetailPage() {
  const { dealershipId, contactId } = useParams();
  const navigate = useNavigate();

  const contactQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact", contactId],
    queryFn: () => adminApi.getContact(dealershipId!, contactId!),
    enabled: Boolean(dealershipId && contactId),
  });

  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  if (contactQuery.isLoading) return <PageLoading message="Loading contact…" />;
  if (contactQuery.isError || !contactQuery.data) {
    return (
      <PageError
        title="Couldn't load contact"
        description={contactQuery.error?.message ?? "The contact may have been removed."}
      />
    );
  }

  const contact = contactQuery.data;
  const users = detailQuery.data?.users ?? [];
  const name =
    contact.businessName ||
    [contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
    contact.primaryEmail ||
    contact.primaryPhone ||
    contact.fallbackLabel ||
    "Anonymous";
  // Deep-link into the existing ContactsPage edit dialog, which opens
  // when ?edit=:id is set. Absolute path — route-relative ".." resolves
  // against the route tree, not URL segments.
  const editHref = `/dealerships/${dealershipId}/contacts?edit=${contact.id}`;

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <PageHeader
        eyebrow={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/dealerships/${dealershipId}/contacts`)}
          >
            <ArrowLeft className="h-4 w-4" /> Contacts
          </Button>
        }
        title={name}
        description={[contact.primaryEmail, contact.primaryPhone]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={contact.status === "anonymous" ? "secondary" : "outline"}>
              {contact.status}
            </Badge>
            <Button asChild size="sm" variant="outline">
              <Link to={editHref}>
                <Pencil className="h-4 w-4" /> Edit
              </Link>
            </Button>
          </div>
        }
      />

      <PrimaryInfoCard contact={contact} users={users} />

      <ActivityTimelineSection
        dealershipId={dealershipId!}
        contactId={contact.id}
        contactName={name}
        users={users}
      />

      <Tabs defaultValue="interests">
        <div className="overflow-x-auto pb-1">
          <TabsList>
            <TabsTrigger value="interests">Interests</TabsTrigger>
            <TabsTrigger value="garage">Garage</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
            <TabsTrigger value="trades">Trades</TabsTrigger>
            <TabsTrigger value="followups">Follow-ups</TabsTrigger>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            <TabsTrigger value="consent">Consent</TabsTrigger>
            <TabsTrigger value="tags">Tags</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="interests">
          <InterestsTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
        <TabsContent value="garage">
          <GarageTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
        <TabsContent value="appointments">
          <AppointmentsTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
        <TabsContent value="trades">
          <TradesTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
        <TabsContent value="followups">
          <FollowUpsTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
        <TabsContent value="channels">
          <ChannelsTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
        <TabsContent value="consent">
          <ConsentTab dealershipId={dealershipId!} contact={contact} />
        </TabsContent>
        <TabsContent value="tags">
          <TagsTab dealershipId={dealershipId!} contactId={contact.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primary information
// ---------------------------------------------------------------------------

// The dealership detail DTO delivers full users — carry the name parts
// (and kind/avatar) so labels come from userDisplayLabel and AI actors
// are detectable, instead of a displayName→email→raw-id chain.
type UserSummary = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  kind: string;
};

function PrimaryInfoCard({
  contact,
  users,
}: {
  contact: Contact;
  users: UserSummary[];
}) {
  const assignedUser = contact.assignedUserId
    ? users.find((u) => u.id === contact.assignedUserId)
    : undefined;
  const assignedRep = contact.assignedUserId
    ? assignedUser
      ? userDisplayLabel(assignedUser)
      : "—"
    : "Unassigned";
  const addressLine = [contact.addressLine1, contact.city, contact.state, contact.zip]
    .filter((v) => v && String(v).trim().length > 0)
    .join(", ");
  const attribution = [contact.utmSource, contact.utmMedium, contact.utmCampaign]
    .filter((v) => v && String(v).trim().length > 0)
    .join(" / ");

  return (
    <Section
      title="Primary information"
      description={contact.businessAccount ? "Business account" : "Individual"}
    >
      <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1.5 rounded-md bg-muted/40 px-3 py-2">
        <Snapshot label="Assigned" value={assignedRep} />
        <Snapshot
          label="Last activity"
          value={
            contact.lastActivityAt
              ? formatRelativeTime(contact.lastActivityAt)
              : "No activity"
          }
        />
        <Snapshot label="Customer since" value={formatDate(contact.createdAt)} />
        {contact.leadSource ? (
          <Snapshot label="Lead source" value={contact.leadSource} />
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {contact.businessAccount && (
          <Field label="Business name">{contact.businessName ?? "—"}</Field>
        )}
        <Field label="Name">
          {[contact.firstName, contact.lastName].filter(Boolean).join(" ") || "—"}
        </Field>
        <Field label="Email">{contact.primaryEmail ?? "—"}</Field>
        <Field label="Phone">{contact.primaryPhone ?? "—"}</Field>
        <Field label="Birth date">
          {contact.birthDate ? formatDate(contact.birthDate) : "—"}
        </Field>
        <Field label="Assigned rep">
          {assignedUser ? (
            <span className="flex items-center gap-2">
              <ContactAvatar
                size="sm"
                name={assignedRep}
                avatarUrl={assignedUser.avatarUrl}
              />
              {assignedRep}
            </span>
          ) : (
            assignedRep
          )}
        </Field>
        <Field label="Status">
          <Badge variant={contact.status === "anonymous" ? "secondary" : "outline"}>
            {contact.status}
          </Badge>
        </Field>
        <Field label="Address">{addressLine || "—"}</Field>
        <Field label="Preferred channel">{contact.preferredChannel ?? "—"}</Field>
        <Field label="Language">{contact.preferredLanguage ?? "—"}</Field>
        <Field label="Timezone">{contact.timezone ?? "—"}</Field>
        <Field label="Lead source">{contact.leadSource ?? "—"}</Field>
        <Field label="Attribution">{attribution || "—"}</Field>
        <Field label="Last activity">
          {contact.lastActivityAt ? formatDateTime(contact.lastActivityAt) : "—"}
        </Field>
        <Field label="Customer since">{formatDateTime(contact.createdAt)}</Field>
        <Field label="Consent">
          <div className="flex flex-wrap gap-1">
            <ConsentChip label="email" optedOut={contact.optoutEmail} />
            <ConsentChip label="sms" optedOut={contact.optoutSms} />
            <ConsentChip label="voice" optedOut={contact.optoutVoice} />
          </div>
        </Field>
        {contact.notes && (
          <Field className="sm:col-span-2 lg:col-span-3" label="Notes">
            <p className="whitespace-pre-wrap text-sm">{contact.notes}</p>
          </Field>
        )}
        {contact.internalNotes && (
          <Field className="sm:col-span-2 lg:col-span-3" label="Internal notes">
            <p className="whitespace-pre-wrap text-sm">{contact.internalNotes}</p>
          </Field>
        )}
      </div>
    </Section>
  );
}

function ConsentChip({ label, optedOut }: { label: string; optedOut: boolean }) {
  return (
    <Badge
      variant={optedOut ? "destructive" : "outline"}
      className="text-[10px] uppercase"
    >
      {label} {optedOut ? "out" : "ok"}
    </Badge>
  );
}

function Snapshot({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity timeline — unified feed across leads, trades,
// appointments, interests, follow-ups, consent events, and system
// activity events. Mirrors Contact360Page so admin sees the same
// chronological story the dealer does.
// ---------------------------------------------------------------------------

function ActivityTimelineSection({
  dealershipId,
  contactId,
  contactName,
  users,
}: {
  dealershipId: string;
  contactId: string;
  contactName: string;
  users: UserSummary[];
}) {
  const navigate = useNavigate();
  const conversationsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact-leads", contactId],
    queryFn: async () => {
      // Leads are filtered server-side by contactId; we ask
      // for both open and closed so the timeline is complete.
      const [open, closed] = await Promise.all([
        adminApi.listLeads(dealershipId, {
          contactId,
          closed: "false",
          includeAnonymous: true,
        }),
        adminApi.listLeads(dealershipId, {
          contactId,
          closed: "true",
          includeAnonymous: true,
        }),
      ]);
      return [...open.items, ...closed.items];
    },
  });
  // Timeline sources are bounded (capped page) instead of pulling every
  // row — the merged feed only needs the most recent slice per source.
  const tradesQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "trade-ins", contactId, "timeline"],
    queryFn: () =>
      adminApi.listTradeIns(dealershipId, contactId, {
        pageSize: TIMELINE_PAGE_SIZE,
      }),
  });
  const appointmentsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "appointments", contactId, "timeline"],
    queryFn: () =>
      adminApi.listAppointments(dealershipId, {
        contactId,
        pageSize: TIMELINE_PAGE_SIZE,
      }),
  });
  const interestsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "vehicle-interests", contactId, "timeline"],
    queryFn: () =>
      adminApi.listVehicleInterests(dealershipId, contactId, {
        pageSize: TIMELINE_PAGE_SIZE,
      }),
  });
  const followUpsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "follow-ups", contactId, "timeline"],
    queryFn: () =>
      adminApi.listFollowUps(dealershipId, contactId, {
        pageSize: TIMELINE_PAGE_SIZE,
      }),
  });
  const consentQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "consent-events", contactId, "timeline"],
    queryFn: () =>
      adminApi.listConsentEvents(dealershipId, contactId, {
        pageSize: TIMELINE_PAGE_SIZE,
      }),
  });
  const activitiesQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact-activities", contactId],
    queryFn: () =>
      adminApi.listBdcActivities(dealershipId, { contactId, pageSize: 100 }),
  });

  // Keep the full user objects — describeActor needs `kind` to credit
  // AI personas as "AI assistant", and labels go through
  // userDisplayLabel so a nameless user degrades to email/phone, never
  // a raw UUID.
  const usersById = useMemo(() => {
    const m = new Map<string, UserSummary>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const isLoading =
    conversationsQuery.isLoading ||
    tradesQuery.isLoading ||
    appointmentsQuery.isLoading ||
    interestsQuery.isLoading ||
    followUpsQuery.isLoading ||
    consentQuery.isLoading ||
    activitiesQuery.isLoading;
  const isError =
    conversationsQuery.isError ||
    tradesQuery.isError ||
    appointmentsQuery.isError ||
    interestsQuery.isError ||
    followUpsQuery.isError ||
    consentQuery.isError ||
    activitiesQuery.isError;

  const entries = useMemo<TimelineEntry[]>(() => {
    const out: TimelineEntry[] = [];
    const conversationsById = new Map<string, Lead>();
    for (const c of conversationsQuery.data ?? []) conversationsById.set(c.id, c);
    const segments = buildLeadSegments(
      activitiesQuery.data?.items ?? [],
      conversationsById,
      (id) => {
        const u = usersById.get(id);
        return u ? userDisplayLabel(u) : null;
      },
    );
    for (const seg of segments) out.push(seg);
    for (const t of tradesQuery.data?.items ?? []) {
      out.push({ kind: "trade", occurredAt: t.createdAt, trade: t });
    }
    // The appointments endpoint doesn't filter by contactId server-side
    // for admin, so trim here.
    for (const a of (appointmentsQuery.data?.items ?? []).filter(
      (a) => a.contactId === contactId,
    )) {
      out.push({
        kind: "appointment",
        occurredAt: a.scheduledStartAt,
        appointment: a,
      });
    }
    for (const i of interestsQuery.data?.items ?? []) {
      out.push({
        kind: "interest",
        occurredAt: i.lastMentionedAt ?? i.createdAt,
        interest: i,
      });
    }
    for (const f of followUpsQuery.data?.items ?? []) {
      out.push({ kind: "followup", occurredAt: f.dueAt, followUp: f });
    }
    for (const e of consentQuery.data?.items ?? []) {
      out.push({ kind: "consent", occurredAt: e.occurredAt, consent: e });
    }
    for (const a of activitiesQuery.data?.items ?? []) {
      if (!SYSTEM_ACTIVITY_TYPES.has(a.type)) continue;
      out.push({ kind: "system", occurredAt: a.occurredAt, activity: a });
    }
    out.sort(
      (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    );
    return out;
  }, [
    conversationsQuery.data,
    tradesQuery.data,
    appointmentsQuery.data,
    interestsQuery.data,
    followUpsQuery.data,
    consentQuery.data,
    activitiesQuery.data,
    contactId,
    usersById,
  ]);

  const grouped = useMemo(() => groupTimelineByBucket(entries), [entries]);

  return (
    <Section
      title="Activity timeline"
      description="Leads, trades, appointments, interests, follow-ups, and consent events in one feed."
    >
      {isLoading ? (
        <PageLoading message="Loading activity…" />
      ) : isError ? (
        <PageError
          title="Couldn't load activity"
          description="Try refreshing the page."
        />
      ) : grouped.length === 0 ? (
        <EmptyState
          title="No activity yet"
          description="Once this contact has leads, trades, or follow-ups, they show up here."
        />
      ) : (
        <div className="flex flex-col gap-5">
          {grouped.map((bucket) => (
            <div key={bucket.label} className="flex flex-col gap-2">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {bucket.label}
              </h4>
              <ul className="flex flex-col gap-2">
                {bucket.entries.map((entry) => (
                  <TimelineEntryRow
                    key={timelineEntryKey(entry)}
                    entry={entry}
                    users={usersById}
                    contactName={contactName}
                    onOpenLead={(id) =>
                      navigate(`/dealerships/${dealershipId}/inbox/${id}`)
                    }
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

type TimelineEntry =
  | {
      kind: "conv-segment";
      occurredAt: string;
      leadId: string;
      channel: string;
      lastPreview: string;
      messageCount: number;
      closed: boolean;
      unreadAt: string | null;
      lastActorUserId: string | null;
      lastActorType: "user" | "ai" | "contact" | "system" | null;
    }
  | { kind: "trade"; occurredAt: string; trade: TradeIn }
  | { kind: "appointment"; occurredAt: string; appointment: Appointment }
  | { kind: "interest"; occurredAt: string; interest: VehicleInterest }
  | { kind: "followup"; occurredAt: string; followUp: FollowUp }
  | { kind: "consent"; occurredAt: string; consent: ConsentEvent }
  | { kind: "system"; occurredAt: string; activity: Activity };

const SYSTEM_ACTIVITY_TYPES = new Set([
  "assignment_change",
  "profile_update",
  "internal_note",
  "status_change",
]);

function buildLeadSegments(
  activities: Activity[],
  conversationsById: Map<string, Lead>,
  userName: (id: string) => string | null,
): TimelineEntry[] {
  const segments: TimelineEntry[] = [];
  const open = new Map<
    string,
    {
      firstAt: string;
      lastAt: string;
      count: number;
      lastPreview: string;
      lastActorUserId: string | null;
      lastActorType: "user" | "ai" | "contact" | "system" | null;
    }
  >();
  const sortedAsc = [...activities].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
  );

  const flush = (leadId: string) => {
    const seg = open.get(leadId);
    if (!seg) return;
    const lead = conversationsById.get(leadId);
    segments.push({
      kind: "conv-segment",
      occurredAt: seg.lastAt,
      leadId,
      channel: lead?.primaryChannel ?? "unknown",
      lastPreview: seg.lastPreview,
      messageCount: seg.count,
      closed: lead?.closed ?? false,
      unreadAt: lead?.unreadAt ?? null,
      lastActorUserId: seg.lastActorUserId,
      lastActorType: seg.lastActorType,
    });
    open.delete(leadId);
  };

  for (const a of sortedAsc) {
    if (a.type === "message" && a.leadId) {
      const seg = open.get(a.leadId) ?? {
        firstAt: a.occurredAt,
        lastAt: a.occurredAt,
        count: 0,
        lastPreview: "",
        lastActorUserId: null,
        lastActorType: null,
      };
      seg.lastAt = a.occurredAt;
      seg.count += 1;
      // A change-diff body renders as explicit before → after text;
      // plain messages keep the summary/body fallback.
      seg.lastPreview =
        summarizeChangeBody(a.body, { userName }) ??
        a.summary ??
        a.body ??
        "(no preview)";
      seg.lastActorUserId = a.actorUserId;
      seg.lastActorType = a.actorType as typeof seg.lastActorType;
      open.set(a.leadId, seg);
    } else if (SYSTEM_ACTIVITY_TYPES.has(a.type)) {
      if (a.leadId) flush(a.leadId);
    }
  }
  for (const id of Array.from(open.keys())) flush(id);
  return segments;
}

function timelineEntryKey(entry: TimelineEntry): string {
  switch (entry.kind) {
    case "conv-segment":
      return `cs-${entry.leadId}-${entry.occurredAt}`;
    case "trade":
      return `t-${entry.trade.id}`;
    case "appointment":
      return `a-${entry.appointment.id}`;
    case "interest":
      return `i-${entry.interest.id}`;
    case "followup":
      return `f-${entry.followUp.id}`;
    case "consent":
      return `e-${entry.consent.id}`;
    case "system":
      return `s-${entry.activity.id}`;
  }
}

interface TimelineBucketGroup {
  label: string;
  entries: TimelineEntry[];
}

function groupTimelineByBucket(entries: TimelineEntry[]): TimelineBucketGroup[] {
  const buckets = new Map<string, TimelineBucketGroup>();
  const order: string[] = [];
  const now = Date.now();
  for (const entry of entries) {
    const label = relativeBucket(now - new Date(entry.occurredAt).getTime());
    let bucket = buckets.get(label);
    if (!bucket) {
      bucket = { label, entries: [] };
      buckets.set(label, bucket);
      order.push(label);
    }
    bucket.entries.push(entry);
  }
  return order.map((label) => buckets.get(label)!);
}

function relativeBucket(deltaMs: number): string {
  // Future timestamps (e.g. upcoming appointments) get their own bucket
  // instead of masquerading as "TODAY".
  if (deltaMs < 0) return "UPCOMING";
  const sec = Math.floor(deltaMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day < 1) return "TODAY";
  if (day < 2) return "YESTERDAY";
  if (day < 7) return `${day} DAYS AGO`;
  if (day < 30) {
    const weeks = Math.floor(day / 7);
    return weeks <= 1 ? "1 WEEK AGO" : `${weeks} WEEKS AGO`;
  }
  if (day < 365) {
    const months = Math.floor(day / 30);
    return months <= 1 ? "1 MONTH AGO" : `${months} MONTHS AGO`;
  }
  return "MORE THAN A YEAR AGO";
}

interface TimelineKindVisual {
  Icon: LucideIcon;
  bg: string;
  fg: string;
}
const TIMELINE_KIND_VISUAL: Record<TimelineEntry["kind"], TimelineKindVisual> = {
  "conv-segment": { Icon: MessageSquare, bg: "bg-blue-50", fg: "text-blue-700" },
  trade: { Icon: Car, bg: "bg-amber-50", fg: "text-amber-700" },
  appointment: { Icon: CalendarCheck, bg: "bg-indigo-50", fg: "text-indigo-700" },
  interest: { Icon: Heart, bg: "bg-rose-50", fg: "text-rose-700" },
  followup: { Icon: BellRing, bg: "bg-emerald-50", fg: "text-emerald-700" },
  consent: { Icon: ShieldCheck, bg: "bg-teal-50", fg: "text-teal-700" },
  system: { Icon: Settings, bg: "bg-slate-100", fg: "text-slate-600" },
};

const SYSTEM_TYPE_VISUAL: Record<
  string,
  { Icon: LucideIcon; bg: string; fg: string; label: string }
> = {
  assignment_change: {
    Icon: UserCheck,
    bg: "bg-cyan-50",
    fg: "text-cyan-700",
    label: "Assignment changed",
  },
  profile_update: {
    Icon: Pencil,
    bg: "bg-violet-50",
    fg: "text-violet-700",
    label: "Profile updated",
  },
  internal_note: {
    Icon: StickyNote,
    bg: "bg-yellow-50",
    fg: "text-yellow-700",
    label: "Internal note",
  },
  status_change: {
    Icon: Settings,
    bg: "bg-orange-50",
    fg: "text-orange-700",
    label: "Status changed",
  },
};

function vehicleLabel(parts: {
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  vin?: string | null;
}): string {
  const composed = [parts.year, parts.make, parts.model, parts.trim]
    .filter(Boolean)
    .join(" ");
  return composed || parts.vin || "Unspecified vehicle";
}

// AI text replies are written with actorType="user" + the AI user's id,
// so `kind` — not actorType — is the reliable AI signal. Contact rows
// name the actual customer, never the literal "contact".
function describeActor(
  entry: TimelineEntry,
  users: Map<string, UserSummary>,
  contactName: string,
): string | null {
  if (entry.kind === "system") {
    const id = entry.activity.actorUserId;
    if (!id) return entry.activity.actorType === "system" ? "by system" : null;
    const actor = users.get(id);
    if (actor?.kind === "ai") return "by AI assistant";
    return `by ${userDisplayLabel(actor)}`;
  }
  if (entry.kind === "conv-segment") {
    if (entry.lastActorType === "contact") return `by ${contactName}`;
    const actor = entry.lastActorUserId
      ? users.get(entry.lastActorUserId)
      : undefined;
    if (entry.lastActorType === "ai" || actor?.kind === "ai") {
      return "by AI assistant";
    }
    if (entry.lastActorUserId) return `by ${userDisplayLabel(actor)}`;
    return null;
  }
  return null;
}

function TimelineEntryRow({
  entry,
  onOpenLead,
  users,
  contactName,
}: {
  entry: TimelineEntry;
  onOpenLead: (leadId: string) => void;
  users: Map<string, UserSummary>;
  contactName: string;
}) {
  const visual =
    entry.kind === "system"
      ? SYSTEM_TYPE_VISUAL[entry.activity.type] ?? TIMELINE_KIND_VISUAL.system
      : TIMELINE_KIND_VISUAL[entry.kind];
  const Icon = visual.Icon;

  let title: string;
  let preview: ReactNode;
  let badges: ReactNode = null;
  let onClick: (() => void) | undefined;
  switch (entry.kind) {
    case "conv-segment": {
      title = `Lead · ${entry.channel}`;
      preview = entry.lastPreview;
      badges = (
        <>
          <Badge variant="outline" className="text-[10px] uppercase">
            {entry.messageCount} {entry.messageCount === 1 ? "msg" : "msgs"}
          </Badge>
          {entry.closed ? (
            <Badge variant="secondary" className="text-[10px] uppercase">
              closed
            </Badge>
          ) : null}
          {entry.unreadAt ? (
            <span
              aria-label="Unread"
              className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500"
            />
          ) : null}
        </>
      );
      onClick = () => onOpenLead(entry.leadId);
      break;
    }
    case "trade": {
      const t = entry.trade;
      title = `Trade-in · ${vehicleLabel(t)}`;
      preview =
        [
          t.condition ? `condition ${t.condition}` : null,
          t.mileage !== null ? `${t.mileage.toLocaleString()} mi` : null,
          t.offeredValue !== null
            ? `offered $${t.offeredValue.toLocaleString()}`
            : t.expectedValue !== null
              ? `expects $${t.expectedValue.toLocaleString()}`
              : null,
        ]
          .filter(Boolean)
          .join(" · ") || "trade-in submitted";
      badges = (
        <Badge variant="outline" className="text-[10px] uppercase">
          {t.status}
        </Badge>
      );
      break;
    }
    case "appointment": {
      const a = entry.appointment;
      title = `Appointment · ${a.appointmentType}`;
      preview = [
        formatDateTime(a.scheduledStartAt),
        a.transportType ? `transport ${a.transportType}` : null,
        a.notes ?? null,
      ]
        .filter(Boolean)
        .join(" · ");
      badges = (
        <Badge variant="outline" className="text-[10px] uppercase">
          {a.status}
        </Badge>
      );
      break;
    }
    case "interest": {
      const i = entry.interest;
      title = `Interest · ${vehicleLabel(i)}`;
      preview = i.notes ?? `interest level ${i.interestLevel ?? "—"}`;
      badges = (
        <Badge variant="outline" className="text-[10px] uppercase">
          {i.status}
        </Badge>
      );
      break;
    }
    case "followup": {
      const f = entry.followUp;
      title = `Follow-up · ${f.kind}`;
      preview = f.reason ?? `due ${formatDateTime(f.dueAt)}`;
      badges = (
        <Badge variant="outline" className="text-[10px] uppercase">
          {f.status}
        </Badge>
      );
      break;
    }
    case "consent": {
      const e = entry.consent;
      title = `Consent · ${e.event}`;
      preview = `${e.channelType} · ${e.scope} via ${e.source}`;
      break;
    }
    case "system": {
      const a = entry.activity;
      const spec = SYSTEM_TYPE_VISUAL[a.type];
      title = spec?.label ?? humanize(a.type);
      // Change-diff bodies render as explicit before → after text
      // instead of the terse summary ("Status: Working → Lost").
      preview =
        summarizeChangeBody(a.body, {
          userName: (id) => {
            const u = users.get(id);
            return u ? userDisplayLabel(u) : null;
          },
        }) ??
        a.summary ??
        "(no details)";
      break;
    }
  }

  const interactive = onClick !== undefined;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={!interactive}
        className={cn(
          "flex w-full items-start justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm transition-colors",
          interactive ? "hover:bg-muted/60 cursor-pointer" : "cursor-default",
        )}
      >
        <span className="flex min-w-0 flex-1 items-start gap-3">
          <span
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
              visual.bg,
              visual.fg,
            )}
            aria-hidden
          >
            <Icon className="h-4 w-4" />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-foreground">{title}</span>
              {badges}
            </span>
            <span className="line-clamp-2 text-muted-foreground">{preview}</span>
            {(() => {
              const actor = describeActor(entry, users, contactName);
              return actor ? (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {actor}
                </span>
              ) : null;
            })()}
          </span>
        </span>
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {formatDateTime(entry.occurredAt)}
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Tabs — read-only lists matching the dealer-app Contact 360
// ---------------------------------------------------------------------------

function InterestsTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const interests = usePagedTabList(
    ["admin-bdc", dealershipId, "vehicle-interests", contactId, "tab"],
    (page) =>
      adminApi.listVehicleInterests(dealershipId, contactId, {
        page,
        pageSize: TAB_PAGE_SIZE,
      }),
  );
  const items = interests.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section
      title="Vehicle interests"
      description="Vehicles this contact has shopped for."
    >
      {interests.isLoading ? (
        <PageLoading message="Loading interests…" />
      ) : interests.isError ? (
        <PageError
          title="Couldn't load interests"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No interests yet"
          description="Nothing recorded — the contact hasn't expressed interest in a specific vehicle."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((i) => (
            <InterestRow key={i.id} interest={i} />
          ))}
          <ShowMore
            hasNextPage={!!interests.hasNextPage}
            isFetchingNextPage={interests.isFetchingNextPage}
            onClick={() => void interests.fetchNextPage()}
          />
        </ul>
      )}
    </Section>
  );
}

function InterestRow({ interest }: { interest: VehicleInterest }) {
  const label = [interest.year, interest.make, interest.model, interest.trim]
    .filter(Boolean)
    .join(" ");
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div>
        <div className="font-medium">
          {label || interest.vin || "Unspecified vehicle"}
        </div>
        <div className="text-xs text-muted-foreground">
          {interest.interestLevel ?? "—"} · status {interest.status}
        </div>
      </div>
      <span className="text-xs text-muted-foreground">
        {formatDateTime(interest.lastMentionedAt ?? interest.createdAt)}
      </span>
    </li>
  );
}

function GarageTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const vehicles = usePagedTabList(
    ["admin-bdc", dealershipId, "contact-vehicles", contactId, "tab"],
    (page) =>
      adminApi.listContactVehicles(dealershipId, contactId, {
        page,
        pageSize: TAB_PAGE_SIZE,
      }),
  );
  const items = vehicles.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section
      title="Garage"
      description="Vehicles this contact owns, leases, or used to own."
    >
      {vehicles.isLoading ? (
        <PageLoading message="Loading garage…" />
      ) : vehicles.isError ? (
        <PageError
          title="Couldn't load garage"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="Empty garage"
          description="No owned or previously-owned vehicles on file."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((v) => (
            <GarageRow key={v.id} vehicle={v} />
          ))}
          <ShowMore
            hasNextPage={!!vehicles.hasNextPage}
            isFetchingNextPage={vehicles.isFetchingNextPage}
            onClick={() => void vehicles.fetchNextPage()}
          />
        </ul>
      )}
    </Section>
  );
}

function GarageRow({ vehicle }: { vehicle: ContactVehicle }) {
  const label =
    [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
      .filter(Boolean)
      .join(" ") || vehicle.vin || "Unspecified vehicle";
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">
          {vehicle.relationship}
          {vehicle.mileage !== null ? ` · ${vehicle.mileage.toLocaleString()} mi` : ""}
          {vehicle.purchasedFromThisDealer ? " · bought here" : ""}
        </div>
      </div>
      <span className="text-xs text-muted-foreground">
        {formatDateTime(vehicle.nextServiceDueAt ?? vehicle.lastServiceAt)}
      </span>
    </li>
  );
}

function AppointmentsTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const apptQuery = usePagedTabList(
    ["admin-bdc", dealershipId, "appointments", contactId, "tab"],
    (page) =>
      adminApi.listAppointments(dealershipId, {
        contactId,
        page,
        pageSize: TAB_PAGE_SIZE,
      }),
  );
  const items = apptQuery.data?.pages.flatMap((p) => p.items) ?? [];
  // Title shows the server-side total, not just the loaded pages.
  const total = apptQuery.data?.pages[0]?.total ?? items.length;

  return (
    <Section title={`${total} appointment${total === 1 ? "" : "s"}`}>
      {apptQuery.isLoading ? (
        <PageLoading message="Loading appointments…" />
      ) : apptQuery.isError ? (
        <PageError
          title="Couldn't load appointments"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No appointments"
          description="This contact has no appointments on the calendar."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-start justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{humanize(a.appointmentType)}</span>
                  <Badge variant="outline">{humanize(a.status)}</Badge>
                </div>
                {a.notes && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {a.notes}
                  </div>
                )}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatDateTime(a.scheduledStartAt)}
              </span>
            </li>
          ))}
          <ShowMore
            hasNextPage={!!apptQuery.hasNextPage}
            isFetchingNextPage={apptQuery.isFetchingNextPage}
            onClick={() => void apptQuery.fetchNextPage()}
          />
        </ul>
      )}
    </Section>
  );
}

function TradesTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const trades = usePagedTabList(
    ["admin-bdc", dealershipId, "trade-ins", contactId, "tab"],
    (page) =>
      adminApi.listTradeIns(dealershipId, contactId, {
        page,
        pageSize: TAB_PAGE_SIZE,
      }),
  );
  const items = trades.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section title="Trade-ins" description="Trade submissions and appraisals.">
      {trades.isLoading ? (
        <PageLoading message="Loading trades…" />
      ) : trades.isError ? (
        <PageError
          title="Couldn't load trade-ins"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No trade-ins yet"
          description="No trade leads have been captured for this contact."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((t) => (
            <TradeRow key={t.id} trade={t} />
          ))}
          <ShowMore
            hasNextPage={!!trades.hasNextPage}
            isFetchingNextPage={trades.isFetchingNextPage}
            onClick={() => void trades.fetchNextPage()}
          />
        </ul>
      )}
    </Section>
  );
}

function TradeRow({ trade }: { trade: TradeIn }) {
  const label =
    [trade.year, trade.make, trade.model, trade.trim].filter(Boolean).join(" ") ||
    trade.vin ||
    "Trade-in";
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">
          status {trade.status}
          {trade.condition ? ` · ${trade.condition}` : ""}
          {trade.mileage !== null ? ` · ${trade.mileage.toLocaleString()} mi` : ""}
        </div>
      </div>
      <span className="text-xs text-muted-foreground">
        {trade.offeredValue !== null
          ? `$${trade.offeredValue.toLocaleString()}`
          : trade.expectedValue !== null
            ? `expects $${trade.expectedValue.toLocaleString()}`
            : "—"}
      </span>
    </li>
  );
}

function FollowUpsTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const followUps = usePagedTabList(
    ["admin-bdc", dealershipId, "follow-ups", contactId, "tab"],
    (page) =>
      adminApi.listFollowUps(dealershipId, contactId, {
        page,
        pageSize: TAB_PAGE_SIZE,
      }),
  );
  const items = followUps.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section title="Follow-ups" description="Scheduled outreach for this contact.">
      {followUps.isLoading ? (
        <PageLoading message="Loading follow-ups…" />
      ) : followUps.isError ? (
        <PageError
          title="Couldn't load follow-ups"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No follow-ups"
          description="No reminders are scheduled."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((f) => (
            <FollowUpRow key={f.id} followUp={f} />
          ))}
          <ShowMore
            hasNextPage={!!followUps.hasNextPage}
            isFetchingNextPage={followUps.isFetchingNextPage}
            onClick={() => void followUps.fetchNextPage()}
          />
        </ul>
      )}
    </Section>
  );
}

function FollowUpRow({ followUp }: { followUp: FollowUp }) {
  const overdue =
    followUp.status === "pending" && new Date(followUp.dueAt) < new Date();
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div>
        <div className="font-medium">
          {followUp.kind} · {followUp.reason ?? "follow-up"}
        </div>
        <div className="text-xs text-muted-foreground">
          due {formatDateTime(followUp.dueAt)} · status {followUp.status}
        </div>
      </div>
      {overdue && (
        <Badge variant="destructive" className="text-[10px] uppercase">
          Overdue
        </Badge>
      )}
    </li>
  );
}

function ChannelsTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const channelsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact-channels", contactId],
    queryFn: () => adminApi.listContactChannels(dealershipId, contactId),
  });
  const items = channelsQuery.data?.items ?? [];
  return (
    <Section
      title="Connected channels"
      description="External identities this contact reaches the dealership through."
    >
      {channelsQuery.isLoading ? (
        <PageLoading message="Loading channels…" />
      ) : channelsQuery.isError ? (
        <PageError
          title="Couldn't load channels"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No connected channels"
          description="No messaging, phone, or email identities linked yet."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline">{humanize(c.type)}</Badge>
                <span className="font-medium">{c.value ?? c.externalId}</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] uppercase text-muted-foreground">
                {c.isPrimary && <Badge variant="outline">Primary</Badge>}
                {c.isMarketingOptedIn && <Badge variant="outline">Marketing opt-in</Badge>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function ConsentTab({
  dealershipId,
  contact,
}: {
  dealershipId: string;
  contact: Contact;
}) {
  // The consent log is append-only and returned newest-first; offset
  // paging walks older events as the admin asks for more.
  const events = usePagedTabList(
    ["admin-bdc", dealershipId, "consent-events", contact.id, "tab"],
    (page) =>
      adminApi.listConsentEvents(dealershipId, contact.id, {
        page,
        pageSize: TAB_PAGE_SIZE,
      }),
  );
  const items = events.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Section
      title="Consent log"
      description="Append-only TCPA paper trail. Every opt-in and opt-out is logged with evidence."
    >
      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <ConsentSummary label="Email" optedOut={contact.optoutEmail} />
        <ConsentSummary label="SMS" optedOut={contact.optoutSms} />
        <ConsentSummary label="Voice" optedOut={contact.optoutVoice} />
      </div>
      {events.isLoading ? (
        <PageLoading message="Loading consent log…" />
      ) : events.isError ? (
        <PageError
          title="Couldn't load consent log"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No consent events"
          description="No opt-in or opt-out events recorded."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((e) => (
            <ConsentRow key={e.id} event={e} />
          ))}
          <ShowMore
            hasNextPage={!!events.hasNextPage}
            isFetchingNextPage={events.isFetchingNextPage}
            onClick={() => void events.fetchNextPage()}
          />
        </ul>
      )}
    </Section>
  );
}

function ConsentSummary({ label, optedOut }: { label: string; optedOut: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <span className="font-medium">{label}</span>
      <Badge variant={optedOut ? "destructive" : "outline"}>
        {optedOut ? "opted out" : "ok"}
      </Badge>
    </div>
  );
}

function ConsentRow({ event }: { event: ConsentEvent }) {
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm">
      <div>
        <div className="font-medium">
          {event.event} · {event.channelType} ({event.scope})
        </div>
        <div className="text-xs text-muted-foreground">
          via {event.source} · {formatDateTime(event.occurredAt)}
        </div>
      </div>
    </li>
  );
}

function TagsTab({
  dealershipId,
  contactId,
}: {
  dealershipId: string;
  contactId: string;
}) {
  const tagsQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact-tags", contactId],
    queryFn: () => adminApi.listContactTags(dealershipId, contactId),
  });
  const items = tagsQuery.data ?? [];
  return (
    <Section title="Tags" description="Tags the dealership has applied to this contact.">
      {tagsQuery.isLoading ? (
        <PageLoading message="Loading tags…" />
      ) : tagsQuery.isError ? (
        <PageError
          title="Couldn't load tags"
          description="Try refreshing the page."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No tags"
          description="No segmentation tags have been applied yet."
        />
      ) : (
        <ul className="grid gap-2">
          {items.map((t: ContactTag) => (
            <li
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2">
                <Plug className="h-4 w-4 text-muted-foreground" aria-hidden />
                {/* The admin API only exposes the tag id — label it so the
                    UUID doesn't pretend to be a tag name. */}
                <span className="text-xs text-muted-foreground">Tag ID</span>
                <span className="font-mono text-xs">{t.tagId}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                applied {formatDateTime(t.appliedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
